// @ts-nocheck
// ═══ Audio-Reactive Particles for Remotion ═══
// Frame-based particle system that responds to audio frequency data.
// No DOM library dependency — pure React rendering per frame.

import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion'
import { useAudioReactivity } from './AudioReactivity'
import { useBeatContext } from './BeatDetection'
import { useMemo } from 'react'

interface ParticleConfig {
  count: number
  color: string
  speed: number
  sizeRange?: [number, number]
  audioReactive?: boolean
}

interface Particle {
  id: number
  x: number
  y: number
  size: number
  baseSpeed: number
  angle: number
  phase: number  // Random phase offset for organic movement
  brightness: number
}

// Deterministic seeded random for consistent particles across frames
function seededRandom(seed: number): number {
  const x = Math.sin(seed * 127.1 + seed * 311.7) * 43758.5453
  return x - Math.floor(x)
}

export const AudioParticles: React.FC<{
  config: ParticleConfig
  opacity?: number
}> = ({ config, opacity = 0.5 }) => {
  const frame = useCurrentFrame()
  const { fps, width, height } = useVideoConfig()
  const { bass, mid, volume } = useAudioReactivity()
  const { isOnBeat, beatIntensity } = useBeatContext()

  const count = config.count || 60
  const color = config.color || '#66ccff'
  const speed = config.speed || 0.3
  const [minSize, maxSize] = config.sizeRange || [1, 4]

  // Generate stable particles (same across frames)
  const particles = useMemo<Particle[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: seededRandom(i * 3 + 1),
      y: seededRandom(i * 3 + 2),
      size: minSize + seededRandom(i * 3 + 3) * (maxSize - minSize),
      baseSpeed: 0.5 + seededRandom(i * 7 + 4) * 1.5,
      angle: seededRandom(i * 7 + 5) * Math.PI * 2,
      phase: seededRandom(i * 7 + 6) * Math.PI * 2,
      brightness: 0.3 + seededRandom(i * 7 + 7) * 0.7,
    }))
  }, [count, minSize, maxSize])

  // Audio-reactive multipliers
  const bassBoost = config.audioReactive !== false
    ? 1 + bass * 2  // Bass makes particles bigger
    : 1
  const volumeSpeed = config.audioReactive !== false
    ? 1 + volume * 1.5  // Volume increases speed
    : 1
  const beatBurst = beatIntensity > 0.3 ? 1 + beatIntensity * 0.5 : 1

  const timeS = frame / fps

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', opacity }}>
      {particles.map(p => {
        // Organic floating movement
        const drift = timeS * speed * p.baseSpeed * volumeSpeed
        const px = ((p.x + Math.sin(drift * 0.7 + p.phase) * 0.05 + drift * 0.01) % 1) * width
        const py = ((p.y + Math.cos(drift * 0.5 + p.phase * 1.3) * 0.04 - drift * 0.008) % 1) * height

        // Audio-reactive size
        const size = p.size * bassBoost * beatBurst

        // Audio-reactive brightness
        const alpha = p.brightness * (0.5 + volume * 0.5)

        return (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: px,
              top: py,
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: color,
              opacity: alpha,
              boxShadow: bass > 0.3
                ? `0 0 ${size * 2}px ${color}80, 0 0 ${size * 4}px ${color}40`
                : `0 0 ${size}px ${color}40`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        )
      })}

      {/* Beat burst — extra bright particles on beat */}
      {beatIntensity > 0.5 && Array.from({ length: 8 }, (_, i) => {
        const burstAngle = (i / 8) * Math.PI * 2 + timeS
        const burstDist = beatIntensity * 80
        const bx = width / 2 + Math.cos(burstAngle) * burstDist
        const by = height / 2 + Math.sin(burstAngle) * burstDist
        return (
          <div
            key={`burst-${i}`}
            style={{
              position: 'absolute',
              left: bx,
              top: by,
              width: 3 + beatIntensity * 4,
              height: 3 + beatIntensity * 4,
              borderRadius: '50%',
              backgroundColor: '#fff',
              opacity: beatIntensity * 0.6,
              boxShadow: `0 0 12px ${color}, 0 0 24px ${color}80`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        )
      })}
    </AbsoluteFill>
  )
}
